/**
 * PostEmbeds — renders the rich embed array attached to a community
 * post. Currently handles film_clip embeds (snapshot/thumbnail +
 * title + body + timestamp pill + Watch + Save-to-Library); designed
 * to extend to drill / playbook / image embeds without restructuring.
 *
 * Each embed is self-contained — the payload carries everything
 * needed to render so the feed stays a single roundtrip.
 */
import React, { useState, useCallback } from "react";
import { DrillPopup } from "./DrillPopup";
import { Play, Plus, CheckCircle, BookmarkPlus, Loader2, ExternalLink, FileText } from "lucide-react";
import { GOLD } from "../constants/theme";
import { sectionLabel } from "../constants/navigation";
import { createFilmFromUrl } from "../film/filmApi";
import { FeatureIcon } from "./FeaturePicker";
import { PLATFORM_BY_ID } from "../constants/platformMap";

function fmtClock(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(r).padStart(2,"0")}`;
  return `${m}:${String(r).padStart(2,"0")}`;
}

function youtubeWatchUrl(yt, t) {
  const id = yt?.youtube_id;
  if (!id) return yt?.youtube_url || "#";
  const sec = Math.max(0, Math.floor(Number(t) || 0));
  return `https://www.youtube.com/watch?v=${id}&t=${sec}s`;
}

export function PostEmbeds({ embeds, C, dark }) {
  if (!Array.isArray(embeds) || embeds.length === 0) return null;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
      {embeds.map((e, i) => {
        if (e?.type === "drill")     return <DrillEmbed    key={i} e={e} C={C} dark={dark}/>;
        // Posts shared before the composer typed its embeds: {id, sectionId,
        // title, tag, duration} with no type. They are drills; draw them.
        if (!e?.type && e?.id != null && e?.sectionId)
          return <DrillEmbed key={i} e={{ drill_id: String(e.id), title: e.title, section: e.sectionId, category: e.tag, duration: e.duration }} C={C} dark={dark}/>;
        if (e?.type === "film_clip") return <FilmClipEmbed key={i} e={e} C={C} dark={dark}/>;
        if (e?.type === "image")     return <ImageEmbed   key={i} e={e} C={C} dark={dark}/>;
        if (e?.type === "video")     return <VideoEmbed   key={i} e={e} C={C} dark={dark}/>;
        if (e?.type === "document")  return <DocEmbed     key={i} e={e} C={C} dark={dark}/>;
        if (e?.type === "weekly_qa") return <WeeklyQAEmbed key={i} e={e} C={C}/>;
        if (e?.type === "feature")   return <FeatureEmbed  key={i} e={e} C={C}/>;
        return null;
      })}
    </div>
  );
}

/**
 * A drill, pointed at rather than described.
 *
 * Thumbnail, title, section, and a way in. No playback id travels in the
 * embed, for one reason: community_posts.embeds is readable with the anon key,
 * which ships in the public bundle, and Mux playback here is unsigned. A
 * playback id in a post row is therefore a working video URL for anyone at
 * all, whether or not they ever had an account.
 *
 * The feed being behind a paywall in the UI does not help — the row is
 * reachable without going through the UI.
 *
 * Watching happens over the feed (DrillPopup); the library, where the
 * writing tools are, is one tap on from there.
 */
function DrillEmbed({ e, C }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
    <button className="btn" onClick={ev => { ev.stopPropagation(); setOpen(true); }}
      style={{display:"flex",alignItems:"stretch",gap:0,width:"100%",textAlign:"left",
        border:`1px solid ${C.border}`,borderRadius:11,overflow:"hidden",cursor:"pointer",
        background:"rgba(255,255,255,0.02)",fontFamily:"inherit",padding:0}}>
      <div style={{width:112,flexShrink:0,minHeight:74,position:"relative",
        background:e.thumbnail_url
          ? `#0b0b0b center/cover url("${e.thumbnail_url}")`
          : "rgba(255,255,255,0.05)"}}>
        <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",
          justifyContent:"center"}}>
          <span style={{width:26,height:26,borderRadius:"50%",background:GOLD,display:"flex",
            alignItems:"center",justifyContent:"center"}}>
            <Play size={12} fill="#111" color="#111" style={{marginLeft:2}}/>
          </span>
        </span>
      </div>
      <div style={{flex:1,minWidth:0,padding:"10px 12px",display:"flex",flexDirection:"column",
        justifyContent:"center",gap:3}}>
        <div style={{fontSize:9.5,fontWeight:800,letterSpacing:1,textTransform:"uppercase",
          color:GOLD}}>Drill</div>
        <div style={{fontSize:14,fontWeight:700,color:C.text,lineHeight:1.3}}>{e.title}</div>
        <div style={{fontSize:11.5,color:C.textDim}}>
          {[sectionLabel(e.section), e.category, e.duration].filter(Boolean).join(" · ") || "Open in the library"}
        </div>
      </div>
    </button>
    {open && <DrillPopup embed={e} C={C} onClose={close}/>}
    </>
  );
}

/**
 * A Weekly Q&A episode. The questions are the pitch: a coach reads three of
 * them and knows whether this week's is theirs. Opening goes through the
 * Resources page, which holds the reader.
 */
// A page, as a card. Reads the live map entry so the words stay current
// even on a post from months ago; falls back to what the post carried.
function FeatureEmbed({ e, C: Cin }) {
  const C = { border: "rgba(255,255,255,0.1)", textDim: "rgba(255,255,255,0.5)", textMid: "rgba(255,255,255,0.7)", text: "#F5F5F5", ...(Cin || {}) };
  const live = PLATFORM_BY_ID[e.feature_id];
  const name = live?.name || e.title || "Open";
  const what = live?.what || e.what || "";
  const route = live?.route || e.route || "#/dashboard";
  const open = () => { window.location.hash = route; };
  return (
    <button className="btn" onClick={open}
      style={{display:"flex",alignItems:"center",gap:12,width:"100%",textAlign:"left",border:`1px solid ${GOLD}33`,borderRadius:11,
        cursor:"pointer",background:`linear-gradient(135deg, ${GOLD}0f, rgba(255,255,255,0.02))`,fontFamily:"inherit",padding:"12px 14px"}}>
      <span style={{width:38,height:38,borderRadius:10,background:`${GOLD}18`,border:`1px solid ${GOLD}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <FeatureIcon id={e.feature_id} size={17}/>
      </span>
      <span style={{flex:1,minWidth:0}}>
        <span style={{display:"block",fontSize:9.5,fontWeight:800,letterSpacing:1,textTransform:"uppercase",color:GOLD}}>On the platform</span>
        <span style={{display:"block",fontSize:14.5,fontWeight:800,color:C.text,lineHeight:1.3}}>{name}</span>
        {what && <span style={{display:"block",fontSize:12.5,color:C.textMid,lineHeight:1.4,marginTop:2}}>{what}</span>}
      </span>
      <ExternalLink size={14} color={GOLD} style={{flexShrink:0}}/>
    </button>
  );
}

function WeeklyQAEmbed({ e, C: Cin }) {
  const C = { border: "rgba(255,255,255,0.1)", textDim: "rgba(255,255,255,0.5)", textMid: "rgba(255,255,255,0.7)", text: "#F5F5F5", ...(Cin || {}) };
  const open = () => {
    try { sessionStorage.setItem("bam_open_qa", JSON.stringify({ id: e.qa_id })); } catch {}
    window.location.hash = "#/resources";
  };
  const when = e.week_of ? new Date(`${e.week_of}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  const qs = Array.isArray(e.questions) ? e.questions : [];
  return (
    <button className="btn" onClick={open}
      style={{display:"block",width:"100%",textAlign:"left",border:`1px solid ${C.border}`,borderRadius:11,
        overflow:"hidden",cursor:"pointer",background:"rgba(255,255,255,0.02)",fontFamily:"inherit",padding:0}}>
      <div style={{display:"flex",alignItems:"stretch"}}>
        <div style={{width:112,flexShrink:0,minHeight:74,position:"relative",
          background:e.thumbnail_url ? `#0b0b0b center/cover url("${e.thumbnail_url}")` : "rgba(255,255,255,0.05)"}}>
          <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{width:26,height:26,borderRadius:"50%",background:GOLD,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Play size={12} fill="#111" color="#111" style={{marginLeft:2}}/>
            </span>
          </span>
        </div>
        <div style={{flex:1,minWidth:0,padding:"10px 12px",display:"flex",flexDirection:"column",justifyContent:"center",gap:3}}>
          <div style={{fontSize:9.5,fontWeight:800,letterSpacing:1,textTransform:"uppercase",color:GOLD}}>Weekly Q&A</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,lineHeight:1.3}}>{e.title}</div>
          <div style={{fontSize:11.5,color:C.textDim}}>
            {[when, e.duration, e.question_count ? `${e.question_count} question${e.question_count === 1 ? "" : "s"}` : null].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>
      {qs.length > 0 && (
        <div style={{borderTop:`1px solid ${C.border}`,padding:"8px 12px 10px",display:"grid",gap:4}}>
          {qs.map((q, i) => (
            <div key={i} style={{fontSize:12.5,color:C.textMid,lineHeight:1.4,display:"flex",gap:7}}>
              <span style={{color:GOLD,flexShrink:0}}>Q</span><span>{q}</span>
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

function FilmClipEmbed({ e, C, dark }) {
  // Save-to-library state — localStorage so the "Saved" state survives
  // a feed reload. Keyed by youtube_id since two coaches sharing the
  // same source film should both flip to Saved once added.
  const savedKey = e?.youtube_id ? `bam_film_saved:${e.youtube_id}` : null;
  const [savedAt, setSavedAt] = useState(() => {
    try { return savedKey ? localStorage.getItem(savedKey) : null; } catch { return null; }
  });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const thumb = e.snapshot_url || e.thumbnail_url;
  const watchHref = youtubeWatchUrl(e, e.timestamp);

  const onSave = async () => {
    if (!e.youtube_url || saving || savedAt) return;
    setSaving(true);
    setSaveErr(null);
    try {
      await createFilmFromUrl({
        url: e.youtube_url,
        title: e.title || "Saved from community",
        kind: e.source_film_kind || "general",
      });
      const ts = new Date().toISOString();
      setSavedAt(ts);
      try { if (savedKey) localStorage.setItem(savedKey, ts); } catch {}
    } catch (err) {
      setSaveErr(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      borderRadius:12,overflow:"hidden",
      border:`1px solid ${dark ? "rgba(226,221,159,0.22)" : "rgba(0,0,0,0.08)"}`,
      background: dark ? "linear-gradient(180deg, rgba(226,221,159,0.04), rgba(0,0,0,0))" : "rgba(226,221,159,0.06)",
    }}>
      {/* Visual */}
      <div style={{position:"relative",background:"#000",aspectRatio:"16/9"}}>
        {thumb ? (
          <img src={thumb} alt={e.title || "Clip"} loading="lazy"
            onError={ev=>{ev.currentTarget.style.display="none";}}
            style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
        ) : (
          <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,0.4)",fontSize:11}}>
            No preview
          </div>
        )}
        {/* Timestamp pill */}
        {e.timestamp != null && (
          <div style={{position:"absolute",bottom:10,left:10,fontSize:11,fontWeight:800,color:"#111",background:GOLD,padding:"4px 10px",borderRadius:6,fontFamily:"'JetBrains Mono', monospace",letterSpacing:.4,boxShadow:"0 4px 14px rgba(0,0,0,0.4)"}}>
            {fmtClock(e.timestamp)}
          </div>
        )}
        {/* Source badge */}
        <div style={{position:"absolute",top:10,left:10,fontSize:9,fontWeight:800,color:"#fff",background:"rgba(0,0,0,0.6)",padding:"3px 8px",borderRadius:5,letterSpacing:.6,textTransform:"uppercase",backdropFilter:"blur(4px)"}}>
          Film clip
        </div>
        {/* Watch CTA — opens YouTube at the right second */}
        <a href={watchHref} target="_blank" rel="noopener noreferrer"
          onClick={ev => ev.stopPropagation()}
          style={{position:"absolute",bottom:10,right:10,display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:7,background:"rgba(0,0,0,0.7)",color:"#fff",fontSize:11.5,fontWeight:700,textDecoration:"none",border:"1px solid rgba(255,255,255,0.18)",backdropFilter:"blur(4px)"}}>
          <Play size={11} fill="#fff"/> Watch <ExternalLink size={10}/>
        </a>
      </div>

      {/* Body */}
      <div style={{padding:"12px 14px"}}>
        {e.title && (
          <div style={{fontSize:13.5,fontWeight:700,color:C.text,lineHeight:1.35}}>{e.title}</div>
        )}
        {e.body && (
          <div style={{fontSize:12.5,color:C.textMid,lineHeight:1.5,marginTop:4,whiteSpace:"pre-wrap"}}>
            {e.body}
          </div>
        )}
        {e.audio_url && (
          <audio controls preload="none" src={e.audio_url}
            onClick={ev => ev.stopPropagation()}
            style={{marginTop:10,width:"100%",height:32,filter: dark ? "invert(0.85) hue-rotate(180deg)" : "none"}}/>
        )}

        {/* Save-to-library — drops the source URL into the viewer's
            own films so they can study + add their own notes. */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:12,paddingTop:10,borderTop:`1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`}}>
          <button onClick={ev => { ev.stopPropagation(); onSave(); }}
            disabled={saving || !!savedAt || !e.youtube_url}
            style={{
              display:"inline-flex",alignItems:"center",gap:6,
              padding:"7px 12px",borderRadius:7,
              background: savedAt ? "rgba(90,181,132,0.10)" : (saving ? "rgba(226,221,159,0.10)" : "transparent"),
              color: savedAt ? "#5AB584" : (saving ? GOLD : C.text),
              border: `1px solid ${savedAt ? "rgba(90,181,132,0.50)" : (dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)")}`,
              fontSize:11.5,fontWeight:700,cursor: (saving || savedAt) ? "default" : "pointer",
              fontFamily:"inherit",
            }}>
            {savedAt ? <CheckCircle size={11}/> : (saving ? <Loader2 size={11} style={{animation:"spin 1s linear infinite"}}/> : <BookmarkPlus size={11}/>)}
            {savedAt ? "Saved to library" : (saving ? "Saving…" : "Save to my library")}
          </button>
          {saveErr && <span style={{fontSize:10.5,color:"#FF8888"}}>{saveErr}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Image / document embeds (community attachments persisted via the
// composer file picker → community-uploads Storage bucket) ────────────

function ImageEmbed({ e, C, dark }) {
  return (
    <a href={e.url} target="_blank" rel="noopener noreferrer"
      onClick={ev => ev.stopPropagation()}
      style={{position:"relative",display:"block",borderRadius:12,overflow:"hidden",minHeight:120,border:`1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}`,background:"#000"}}>
      {/* Fallback behind the image — shown if the image URL is broken/expired. */}
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,0.4)",fontSize:12}}>Image unavailable</div>
      <img src={e.url} alt={e.name || "Image"} loading="lazy"
        onError={ev=>{ev.currentTarget.style.display="none";}}
        onLoad={ev=>{ev.currentTarget.style.opacity=1;}}
        style={{position:"relative",width:"100%",aspectRatio:"4 / 3",maxHeight:420,objectFit:"cover",display:"block",opacity:0,transition:"opacity .3s ease"}}/>
    </a>
  );
}

// Video attachment — native HTML5 player against the public Supabase
// Storage URL. preload="metadata" so the poster frame appears without
// downloading the full clip on feed scroll. No autoplay (feed would
// get chaotic). Coaches click to play. controlsList strips download
// and PiP to keep the surface clean.
function VideoEmbed({ e, C, dark }) {
  return (
    <div
      onClick={ev => ev.stopPropagation()}
      style={{
        borderRadius: 12, overflow: "hidden",
        border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}`,
        background: "#000",
      }}>
      <video
        src={e.url}
        preload="metadata"
        controls
        playsInline
        controlsList="nodownload"
        style={{ width: "100%", maxHeight: 480, display: "block", background: "#000" }}>
        Your browser does not support video playback. <a href={e.url} target="_blank" rel="noopener noreferrer" style={{color:GOLD}}>Download the clip</a>.
      </video>
      {/* The file name and size used to sit under the player. "IceMan Tony.mp4 · 44 MB" is not something a coach wrote for other coaches. */}
    </div>
  );
}

function DocEmbed({ e, C, dark }) {
  return (
    <a href={e.url} target="_blank" rel="noopener noreferrer"
      onClick={ev => ev.stopPropagation()}
      style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:12,
        border:`1px solid ${dark ? "rgba(226,221,159,0.22)" : "rgba(0,0,0,0.08)"}`,
        background: dark ? "rgba(226,221,159,0.04)" : "rgba(226,221,159,0.06)",
        textDecoration:"none",color:C.text}}>
      <div style={{width:36,height:44,borderRadius:6,background:`${GOLD}18`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:GOLD}}>
        <FileText size={18}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.name || "Document"}</div>
        {e.size && <div style={{fontSize:11,color:C.textMid,marginTop:2}}>{fmtBytes(e.size)}</div>}
      </div>
      <span style={{fontSize:11,fontWeight:700,color:GOLD,padding:"5px 12px",borderRadius:6,border:`1px solid ${GOLD}55`,flexShrink:0}}>Open</span>
    </a>
  );
}

function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default PostEmbeds;
